"use client";

import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { createAuthSchemas, SignupSchema } from "@/lib/validation/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/components/ui/input";
import RadioButton from "@/components/atoms/radio-buttons";
import Link from "next/link";
import OptIn from "@/components/atoms/opt-in";
import { cn } from "@/lib/utils";
import { useEffect, useState, useTransition } from "react";
import { Locales } from "@/lib/i18n";
import { __, getPermalink } from "@/lib/i18n/functions";
import { sdk } from "@/lib/medusa/client";
import { FetchError } from "@medusajs/js-sdk";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { toast } from "@/components/atoms/toast";
import { Button } from "@/components/atoms/buttons";
import { useCompanyDetails } from "@/queries/company";

type SignUpFormProps = {
  locale: Locales;
};

const SignUpForm = ({ locale }: SignUpFormProps) => {
  const [isPassVisible, setIsPassVisible] = useState(false);
  const [isConfirmPassVisible, setIsConfirmPassVisible] = useState(false);
  const { signupSchema } = createAuthSchemas(locale);
  const [isPending, startTransition] = useTransition();
  const router = useRouter();
  const searchParams = useSearchParams();

  const token = searchParams.get("token");
  const email = searchParams.get("email");
  const companyName = searchParams.get("company_name");

  const { data: companyDetails } = useCompanyDetails(companyName!);

  const form = useForm<SignupSchema>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      title: undefined,
      firstName: "",
      lastName: "",
      email: email ? email : "",
      phone: "",
      birthDate: "",
      password: "",
      confirmPassword: "",
      subscribeToNewsletter: false,
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = form;

  useEffect(() => {
    if (token && email && companyName) {
      const invitationData = {
        token,
        email,
        company_name: companyName,
        timestamp: Date.now(),
      };

      if (typeof window !== "undefined") {
        sessionStorage.setItem("invitation_data", JSON.stringify(invitationData));
      }
    }
  }, [token, email, companyName]);

  const onSubmit = async (data: SignupSchema) => {
    startTransition(async () => {
      try {
        await sdk.auth.register("customer", "emailpass", {
          email: data.email,
          password: data.password,
        });
      } catch (error) {
        const fetchError = error as FetchError;

        if (fetchError.status === 401 && fetchError.message === "Identity with email already exists") {
          const loginResponse = await sdk.auth
            .login("customer", "emailpass", {
              email: data.email,
              password: data.password,
            }) // Email exists - now test if password is correct
            .catch((e) => {
              console.error(e);
              toast.error({ title: __("An error occured while creating account, Please try again later.", locale) });
            }); // Login failed = wrong password, show generic error

          if (!loginResponse) {
            return;
          } // Login failed

          if (typeof loginResponse !== "string") {
            toast.error({
              title: __("Authentication requires more actions", locale),
              description: __("This isn't supported by this flow.", locale),
            });
            return;
          }

          toast.warning({
            title: __("Account already exists", locale),
            description: __("You already have an account. Please use the login page.", locale),
            button: {
              label: __("Login", locale),
              onClick: () => router.push(getPermalink(locale, "login")),
            },
          }); // Both email AND password are correct - redirect to login
          return;
        } else {
          console.error(fetchError);
          toast.error({ title: __("An error occured while creating account, Please try again later.", locale) });
          return;
        }
      }
      try {
        const { customer } = await sdk.store.customer.create({
          first_name: data.firstName,
          last_name: data.lastName,
          email: data.email,
          phone: data.phone,
          company_name: "",
          metadata: {
            title: data.title,
            birth_date: data.birthDate || "",
            subscribeToNewsletter: data.subscribeToNewsletter,
            verified: false,
            lang: locale,
          },
        });

        if (token && companyDetails && companyDetails.id && customer && customer.id) {
          try {
            await sdk.client.fetch(`store/companies/${companyDetails.id}/invite/process`, {
              method: "POST",
              body: {
                token,
                accepted: true,
                customer_id: customer.id,
              },
            });
            if (typeof window !== "undefined") {
              sessionStorage.removeItem("invitation_data");
            }
          } catch (error) {
            console.error("Error processing invitation:", error);
            toast.warning({
              title: __("Account created successfully, but failed to process company invitation.", locale),
            });
          }
        }

        router.push(`${getPermalink(locale, "signup", "verify")}/${customer.id}`);
      } catch (error) {
        console.error(error);
        toast.error({ title: __("Something went wrong. Please try again later.", locale) });
        return;
      }
    });
    reset();
  };

  return (
    <>
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className="font-geist space-y-6">
          <FormField
            control={control}
            name="title"
            render={({ field }) => (
              <FormItem className="w-full gap-1">
                <div className="flex flex-col gap-2.5">
                  <FormLabel className="mb-0 text-base font-medium">
                    {__("Civility", locale)} <span className="text-pink-primary">*</span>
                  </FormLabel>
                  <RadioButton
                    onValueChange={field.onChange}
                    disabled={isPending}
                    defaultValue={field.value}
                    className="max-sm:justify-evenly"
                    options={[
                      { value: "mademoiselle", label: __("Miss ", locale).trim() },
                      { value: "madame", label: __("Mrs ", locale).trim() },
                      { value: "monsieur", label: __("Mr", locale).trim() },
                    ]}
                  />
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex flex-col items-center gap-6 sm:flex-row sm:gap-4">
            <FormField
              control={control}
              name="firstName"
              render={({ field }) => (
                <FormItem className={cn("w-full gap-1", errors.lastName && !errors.firstName && "sm:mb-6")}>
                  <div className="flex flex-col gap-2.5">
                    <FormLabel className="mb-0 text-base font-medium">
                      {__("First name", locale)} <span className="text-pink-primary">*</span>
                    </FormLabel>

                    <FormControl>
                      <Input
                        disabled={isPending}
                        className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] placeholder:text-sm placeholder:font-medium focus-visible:ring-[2px]"
                        {...field}
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="lastName"
              render={({ field }) => (
                <FormItem className={cn("w-full gap-1", errors.firstName && !errors.lastName && "sm:mb-6")}>
                  <div className="flex flex-col gap-2.5">
                    <FormLabel className="mb-0 text-base font-medium">
                      {__("Last name", locale)} <span className="text-pink-primary">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        disabled={isPending}
                        className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] placeholder:text-sm placeholder:font-medium focus-visible:ring-[2px]"
                        {...field}
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="flex flex-col items-center gap-6 sm:flex-row sm:gap-4">
            <FormField
              control={control}
              name="birthDate"
              render={({ field }) => (
                <FormItem className="w-full gap-1 sm:max-w-[calc(50%-0.5rem)]">
                  <div className="flex flex-col gap-2.5">
                    <FormLabel className="mb-0 text-base font-medium">{__("Date of birth", locale)}</FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        disabled={isPending}
                        className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] placeholder:text-sm placeholder:font-medium focus-visible:ring-[2px]"
                        {...field}
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="flex flex-col items-center gap-6 sm:flex-row sm:gap-4">
            <FormField
              control={control}
              name="email"
              render={({ field }) => (
                <FormItem className={cn("w-full gap-1", errors.phone && !errors.email && "sm:mb-6")}>
                  <div className="flex flex-col gap-2.5">
                    <FormLabel className="mb-0 text-base font-medium">
                      {__("Email Address", locale)} <span className="text-pink-primary">*</span>
                    </FormLabel>

                    <FormControl>
                      <Input
                        type="email"
                        disabled={isPending}
                        className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] placeholder:text-sm placeholder:font-medium focus-visible:ring-[2px]"
                        {...field}
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="phone"
              render={({ field }) => (
                <FormItem className={cn("w-full gap-1", errors.email && !errors.phone && "sm:mb-6")}>
                  <div className="flex flex-col gap-2.5">
                    <FormLabel className="mb-0 text-base font-medium">
                      {__("Phone", locale)} <span className="text-pink-primary">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        disabled={isPending}
                        className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] placeholder:text-sm placeholder:font-medium focus-visible:ring-[2px]"
                        {...field}
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="flex flex-col items-center gap-6 sm:flex-row sm:gap-4">
            <FormField
              control={control}
              name="password"
              render={({ field }) => (
                <FormItem className={cn("w-full gap-1", errors.confirmPassword && !errors.password && "sm:mb-6")}>
                  <div className="flex flex-col gap-2.5">
                    <FormLabel className="mb-0 text-base font-medium">
                      {__("Password", locale)} <span className="text-pink-primary">*</span>
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          disabled={isPending}
                          className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] placeholder:text-sm placeholder:font-medium focus-visible:ring-[2px]"
                          type={isPassVisible ? "text" : "password"}
                          {...field}
                        />
                        <button
                          onClick={() => setIsPassVisible((state) => !state)}
                          type="button"
                          className="absolute inset-y-0 end-0 cursor-pointer pe-3 pl-3"
                        >
                          {isPassVisible ? (
                            <EyeOff size={20} className="text-dark-primary" />
                          ) : (
                            <Eye size={20} className="text-dark-primary" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem className={cn("w-full gap-1", errors.password && !errors.confirmPassword && "sm:mb-6")}>
                  <div className="flex flex-col gap-2.5">
                    <FormLabel className="mb-0 text-base font-medium">
                      {__("Confirm Password", locale)} <span className="text-pink-primary">*</span>
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          disabled={isPending}
                          type={isConfirmPassVisible ? "text" : "password"}
                          className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] placeholder:text-sm placeholder:font-medium focus-visible:ring-[2px]"
                          {...field}
                        />
                        <button
                          onClick={() => setIsConfirmPassVisible((state) => !state)}
                          type="button"
                          className="absolute inset-y-0 end-0 cursor-pointer pe-3 pl-3"
                        >
                          {isConfirmPassVisible ? (
                            <EyeOff size={20} className="text-dark-primary" />
                          ) : (
                            <Eye size={20} className="text-dark-primary" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={control}
            name="subscribeToNewsletter"
            render={({ field }) => (
              <FormItem className={cn("w-full gap-1", errors.lastName && !errors.firstName && "sm:mb-6")}>
                <div className="flex flex-col gap-2.5">
                  <OptIn
                    checked={field.value}
                    disabled={isPending}
                    label={__(
                      "I want to receive Festi tips and inspirations by subscribing to the newsletter.",
                      locale
                    )}
                    onCheckedChange={field.onChange}
                  />
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button disabled={isPending} type="submit" className="h-11 w-full cursor-pointer font-semibold" size="md">
            {isPending ? (
              <>
                <Loader2 className="mr-2 animate-spin" />
              </>
            ) : (
              __("Sign Up", locale)
            )}
          </Button>
        </form>
      </Form>
      <div className="font-geist-sans mt-6 flex items-center justify-between text-sm sm:justify-center sm:gap-3.5">
        <p>
          {__("Already registered ?", locale)}{" "}
          <Link
            href={getPermalink(locale, "login")}
            className="text-pink-primary focus-visible:ring-pink-primary/50 underline outline-none focus-visible:ring-[2px] focus-visible:ring-offset-2"
          >
            {__("Login", locale)}
          </Link>
        </p>
        <hr className="hidden h-[1px] w-2.5 sm:block" />
        <Link
          href={getPermalink(locale, "forgot-password")}
          className="text-pink-primary focus-visible:ring-pink-primary/50 underline outline-none focus-visible:ring-[2px] focus-visible:ring-offset-2"
        >
          {__("Forgot Password ?", locale)}
        </Link>
      </div>
    </>
  );
};

export default SignUpForm;
